/**
 * "Roles that pay about this": from the salary hub's per-role national medians (EUR/month, from
 * disclosed ranges in live postings), the ones closest to a gross monthly salary. Pure, so the
 * page can re-pick as the salary changes without another request.
 */
export interface RoleMedian {
  title: string;
  /** Locale-specific slug under /salary (the Link component localises the base path). */
  slug: string;
  /** National median, EUR/month. */
  median: number;
  sampleSize: number;
}

export interface RolesNearSalaryOptions {
  /** How far from the salary a median may be, as a fraction of the salary. Default 0.15. */
  tolerance?: number;
  /** Roles with fewer postings than this are too noisy to show. Default 2 (the hub's data is
   *  still thin: as of 2026-09 only about 25 roles have three or more salary-disclosing postings). */
  minSampleSize?: number;
  limit?: number;
}

export function rolesNearSalary(roles: RoleMedian[], grossMonthly: number, opts: RolesNearSalaryOptions = {}): RoleMedian[] {
  const tolerance = opts.tolerance ?? 0.15;
  const minSampleSize = opts.minSampleSize ?? 2;
  const limit = opts.limit ?? 8;
  if (!(grossMonthly > 0)) return [];
  const window = grossMonthly * tolerance;
  return roles
    .filter((r) => r.sampleSize >= minSampleSize && Math.abs(r.median - grossMonthly) <= window)
    .sort((a, b) => {
      const da = Math.abs(a.median - grossMonthly);
      const db = Math.abs(b.median - grossMonthly);
      // Closest first; among equally close, the better-evidenced role.
      return da === db ? b.sampleSize - a.sampleSize : da - db;
    })
    .slice(0, limit);
}
